import { formatUiLabel } from '@/utils/displayLabels';

const normalizeText = (value) => {
  const normalized = String(value || '').trim();
  return normalized || '';
};

export const normalizeImportedOrderPlatform = (value) => {
  const normalized = normalizeText(value).toLowerCase();
  if (normalized === 'api') {
    return 'public_api';
  }
  return normalized || 'public_api';
};

const PLATFORM_META = {
  shopify: { label: 'Shopify', className: 'bg-emerald-100 text-emerald-700' },
  woocommerce: { label: 'WooCommerce', className: 'bg-violet-100 text-violet-700' },
  public_api: { label: 'API publique', className: 'bg-slate-100 text-slate-700' },
};

const STATUS_META = {
  imported: { label: 'Importee', className: 'bg-slate-100 text-slate-700' },
  awaiting_validation: { label: 'A valider', className: 'bg-amber-100 text-amber-700' },
  ready_for_conversion: { label: 'A convertir', className: 'bg-blue-100 text-blue-700' },
  converted: { label: 'Convertie', className: 'bg-cyan-100 text-cyan-700' },
  tracking_active: { label: 'Tracking actif', className: 'bg-emerald-100 text-emerald-700' },
  delivered: { label: 'Livree', className: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Annulee', className: 'bg-rose-100 text-rose-700' },
  failed: { label: 'Echec', className: 'bg-red-100 text-red-700' },
};

export const getImportedOrderPlatformMeta = (value) => {
  const normalized = normalizeImportedOrderPlatform(value);
  return PLATFORM_META[normalized] || {
    label: formatUiLabel(normalized) || 'Source externe',
    className: 'bg-slate-100 text-slate-700',
  };
};

export const getImportedOrderStatusMeta = (value) => {
  const normalized = normalizeText(value).toLowerCase();
  return STATUS_META[normalized] || {
    label: formatUiLabel(normalized) || 'Statut',
    className: 'bg-gray-100 text-gray-700',
  };
};

export const getImportedOrderConversionMeta = (order) => {
  if (normalizeText(order?.delivery_id)) {
    return { label: 'Livraison creee', className: 'bg-green-100 text-green-700' };
  }
  const status = normalizeText(order?.status).toLowerCase();
  if (status === 'ready_for_conversion') {
    return { label: 'A convertir', className: 'bg-blue-100 text-blue-700' };
  }
  if (status === 'awaiting_validation') {
    return { label: 'Validation requise', className: 'bg-amber-100 text-amber-700' };
  }
  if (status === 'failed' || status === 'cancelled') {
    return { label: 'Conversion bloquee', className: 'bg-red-100 text-red-700' };
  }
  return { label: 'En attente', className: 'bg-slate-100 text-slate-700' };
};

export const getImportedOrderReadyForPickupMeta = (order) => {
  if (normalizeText(order?.tracking_email_sent_at) || normalizeText(order?.ready_for_pickup_sent_at)) {
    return { label: 'Tracking client envoye', className: 'bg-emerald-100 text-emerald-700' };
  }
  if (normalizeText(order?.delivery_id)) {
    return { label: 'Pret a declencher', className: 'bg-amber-100 text-amber-700' };
  }
  return { label: 'Non declenche', className: 'bg-slate-100 text-slate-700' };
};

export const getImportedOrderReference = (order) => (
  normalizeText(order?.external_order_number)
  || normalizeText(order?.external_order_id)
  || '-'
);

export const getImportedOrderSelectedQuote = (order) => {
  if (order?.selected_quote && typeof order.selected_quote === 'object') {
    return order.selected_quote;
  }
  if (order?.pricing_snapshot && typeof order.pricing_snapshot === 'object') {
    return order.pricing_snapshot;
  }
  return {};
};

export const getImportedOrderQuotes = (order) => {
  const quotes = order?.delivery_quotes;
  if (quotes && typeof quotes === 'object') {
    return {
      standard: quotes.standard && typeof quotes.standard === 'object' ? quotes.standard : {},
      express: quotes.express && typeof quotes.express === 'object' ? quotes.express : {},
    };
  }
  return {
    standard: order?.delivery_quote_standard && typeof order.delivery_quote_standard === 'object' ? order.delivery_quote_standard : {},
    express: order?.delivery_quote_express && typeof order.delivery_quote_express === 'object' ? order.delivery_quote_express : {},
  };
};

export const getImportedOrderTrackingCode = (order) => (
  normalizeText(order?.colinoki_tracking_code)
  || normalizeText(order?.delivery?.tracking_code)
  || ''
);

export const formatImportedOrderMoney = (amount, currency = 'FCFA') => {
  const numericAmount = Number(amount || 0);
  if (!Number.isFinite(numericAmount)) {
    return `0 ${currency}`;
  }
  return `${numericAmount.toLocaleString('fr-FR')} ${currency}`;
};

export const formatImportedOrderDateTime = (value) => {
  const normalized = normalizeText(value);
  if (!normalized) {
    return '-';
  }
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }
  return date.toLocaleString('fr-FR');
};

export const formatImportedOrderDate = (value) => {
  const normalized = normalizeText(value);
  if (!normalized) {
    return '-';
  }
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }
  return date.toLocaleDateString('fr-FR');
};

export const formatImportedOrderAddress = (address) => {
  if (!address || typeof address !== 'object') {
    return '-';
  }
  const parts = [
    address.full_address,
    address.address,
    address.address1,
    address.address2,
    address.district,
    address.city_name,
    address.city,
    address.country_name,
    address.country_code,
  ].map((value) => normalizeText(value)).filter(Boolean);
  return parts.length ? Array.from(new Set(parts)).join(', ') : '-';
};

export const buildImportedOrderSearchText = (order) => [
  order?.external_order_id,
  order?.external_order_number,
  order?.customer_name,
  order?.customer_email,
  order?.customer_phone,
  order?.pickup_city,
  order?.delivery_city,
  order?.status,
  order?.selected_delivery_mode,
  order?.source_platform,
  order?.colinoki_tracking_code,
  order?.delivery_id,
].map((value) => normalizeText(value).toLowerCase()).join(' ');

export const filterRelevantSyncLogs = (logs, order) => {
  const normalizedExternalId = normalizeText(order?.external_order_id).toLowerCase();
  const internalOrderId = normalizeText(order?.id);
  const deliveryId = normalizeText(order?.delivery_id);

  return (logs || []).filter((log) => {
    const externalResourceId = normalizeText(log?.external_resource_id).toLowerCase();
    const internalResourceId = normalizeText(log?.internal_resource_id);
    return Boolean(
      (normalizedExternalId && externalResourceId === normalizedExternalId)
      || (internalOrderId && internalResourceId === internalOrderId)
      || (deliveryId && internalResourceId === deliveryId)
    );
  });
};

export const stringifyImportedOrderPayload = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return '';
  }
  try {
    return JSON.stringify(payload, null, 2);
  } catch (error) {
    return '';
  }
};
